import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { UserPlus, Pencil, Trash2, Users, Search, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  site_manager: 'Site Manager',
  subcontractor: 'Subcontractor',
  inspector: 'Inspector',
};

const ROLE_COLORS = {
  admin: 'bg-rose-100 text-rose-700 border-rose-200',
  project_manager: 'bg-violet-100 text-violet-700 border-violet-200',
  site_manager: 'bg-blue-100 text-blue-700 border-blue-200',
  subcontractor: 'bg-amber-100 text-amber-700 border-amber-200',
  inspector: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const EMPTY_FORM = {
  email: '', password: '', name: '', company: '',
  role: 'subcontractor', packages: [],
};

function PackageMultiSelect({ selected, onChange, options, testId }) {
  return (
    <div className="max-h-40 overflow-y-auto border rounded-md divide-y divide-slate-100" data-testid={testId}>
      {options.map(pkg => {
        const checked = selected.includes(pkg);
        return (
          <label key={pkg} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox
              checked={checked}
              onCheckedChange={() => onChange(checked ? selected.filter(p => p !== pkg) : [...selected, pkg])}
              data-testid={`pkg-${pkg.replace(/[\s/]+/g, '-').toLowerCase()}`}
            />
            <span className="text-slate-700">{pkg}</span>
          </label>
        );
      })}
    </div>
  );
}

function UserForm({ form, setForm, packageOptions, isEdit }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Company</Label>
          <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} data-testid="user-form-company" />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Email</Label>
        <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</Label>
        <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={isEdit ? '••••••••' : 'min 6 chars'} data-testid="user-form-password" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Role</Label>
        <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_LABELS).map(([k, l]) => (
              <SelectItem key={k} value={k}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {form.role === 'subcontractor' && (
        <div className="space-y-1">
          <Label className="text-xs">Packages (subcontractor can edit only these)</Label>
          <PackageMultiSelect
            selected={form.packages}
            onChange={(p) => setForm({ ...form, packages: p })}
            options={packageOptions}
            testId="user-form-packages"
          />
          <p className="text-[11px] text-slate-500">{form.packages.length} package{form.packages.length !== 1 ? 's' : ''} selected</p>
        </div>
      )}
    </div>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [packageOptions, setPackageOptions] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const { data } = await api.get('/packages');
      setPackageOptions(Array.isArray(data) ? data : []);
    } catch {
      setPackageOptions([]);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchPackages(); }, [fetchUsers, fetchPackages]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('Name, email and password are required');
      return;
    }
    try {
      await api.post('/admin/users', form);
      toast.success('User created');
      setAddOpen(false);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email, password: '', name: u.name || '',
      company: u.company || '', role: u.role || 'subcontractor',
      packages: Array.isArray(u.packages) ? u.packages : [],
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!form.email || !form.name) {
      toast.error('Name and email are required');
      return;
    }
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    try {
      await api.put(`/admin/users/${editingId}`, payload);
      toast.success('User updated');
      setEditOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.email || '').toLowerCase().includes(q)
        || (u.name || '').toLowerCase().includes(q)
        || (u.company || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="py-12 text-center text-slate-500" data-testid="user-management-forbidden">
          <Shield className="h-8 w-8 text-rose-500 mx-auto mb-2" />
          Admin access required.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="user-management-page">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
              <Users className="inline h-6 w-6 mr-2 text-blue-600" />User Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">Add, edit, or remove users and assign package responsibilities</p>
          </div>
          <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setForm(EMPTY_FORM); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-button">
                <UserPlus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="add-user-dialog">
              <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
              <UserForm form={form} setForm={setForm} packageOptions={packageOptions} isEdit={false} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="cancel-add-user">Cancel</Button>
                <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-add-user">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, or company..."
              className="pl-9"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(ROLE_LABELS).map(([k, l]) => (
                <SelectItem key={k} value={k}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User list */}
        {(() => {
          if (loading) {
            return <div className="py-16 text-center text-slate-400" data-testid="users-loading">Loading users...</div>;
          }
          if (filtered.length === 0) {
            return <div className="py-16 text-center text-slate-400" data-testid="users-empty">No users match your filters</div>;
          }
          return (
          <div className="space-y-2" data-testid="user-list">
            {filtered.map(u => (
              <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl" data-testid={`user-row-${u.id}`}>
                <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {u.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-900">{u.name}</span>
                    <Badge className={`text-[10px] border ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                    {u.id === currentUser?.id && <Badge variant="outline" className="text-[10px]">You</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{u.email}{u.company ? ` · ${u.company}` : ''}</p>
                  {u.role === 'subcontractor' && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(u.packages || []).length === 0 ? (
                        <span className="text-[10px] text-amber-600 font-medium">⚠ No packages assigned</span>
                      ) : (
                        (u.packages || []).map(p => (
                          <Badge key={p} variant="outline" className="text-[9px] bg-slate-50">{p}</Badge>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50" disabled={u.id === currentUser?.id} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`delete-confirm-${u.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently removes <b>{u.email}</b>. Their audit history will be retained.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(u)} data-testid="confirm-delete">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {/* Edit dialog */}
        <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } }}>
          <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
            <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
            <UserForm form={form} setForm={setForm} packageOptions={packageOptions} isEdit={true} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="cancel-edit-user">Cancel</Button>
              <Button onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-user">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
